import { useCallback, useEffect, useRef, useState } from "react";

import { useFetchApi } from "./useFetchApi";
import { CONVERSATION_PAGE_SIZE } from "../schemas/conversation-list";
import { dedupeById } from "../utils/dedupe-by-id";
import {
  buildConversationPageUrl,
  type ConversationPage,
  type UsePaginatedConversationsArgs,
  type UsePaginatedConversationsResult,
} from "./paginated-conversations-contract";

export type { UsePaginatedConversationsResult } from "./paginated-conversations-contract";

type LoadedPages<TItem> = {
  key: string;
  pages: ConversationPage<TItem>[];
  failed: boolean;
};

export function usePaginatedConversations<TItem extends { id: string }>({
  endpoint,
  debouncedQuery = "",
  extraParams,
  pageSize = CONVERSATION_PAGE_SIZE,
  enabled,
  parsePage,
}: UsePaginatedConversationsArgs<TItem>): UsePaginatedConversationsResult<TItem> {
  const { fetchWeb } = useFetchApi();
  const extraParamsKey = JSON.stringify(extraParams ?? {});
  const [reloadToken, setReloadToken] = useState(0);
  const key = enabled ? `${endpoint}|${debouncedQuery}|${extraParamsKey}|${reloadToken}` : null;
  const [loaded, setLoaded] = useState<LoadedPages<TItem> | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    if (key === null) return;
    let stale = false;
    setLoadMoreFailed(false);
    setIsLoadingMore(false);
    fetchWeb<unknown>(buildConversationPageUrl(endpoint, debouncedQuery, extraParams, pageSize, 0))
      .then((data) => {
        if (!stale) setLoaded({ key, pages: [parsePage(data)], failed: false });
      })
      .catch(() => {
        if (!stale) setLoaded({ key, pages: [], failed: true });
      });
    return () => {
      stale = true;
    };
  }, [key, fetchWeb]);

  const settled = loaded !== null && loaded.key === key;
  const pages = settled ? loaded.pages : [];
  const hasError = settled && loaded.failed;
  const items = dedupeById(pages.flatMap((page) => page.items));
  const isLoadingInitial = key !== null && !settled;

  const lastPage = pages.length > 0 ? pages[pages.length - 1] : null;
  const nextOffset = lastPage ? lastPage.nextOffset ?? null : null;
  const hasMore = nextOffset !== null && !loadMoreFailed;

  const loadMore = useCallback(() => {
    if (key === null || nextOffset === null || isLoadingMore || loadMoreFailed) return;
    setIsLoadingMore(true);
    fetchWeb<unknown>(buildConversationPageUrl(endpoint, debouncedQuery, extraParams, pageSize, nextOffset))
      .then((data) => {
        setLoaded((prev) =>
          prev && prev.key === key
            ? { key, pages: [...prev.pages, parsePage(data)], failed: false }
            : prev
        );
      })
      .catch(() => {
        if (keyRef.current === key) setLoadMoreFailed(true);
      })
      .finally(() => {
        if (keyRef.current === key) setIsLoadingMore(false);
      });
  }, [key, nextOffset, isLoadingMore, loadMoreFailed, endpoint, debouncedQuery, extraParamsKey, pageSize, fetchWeb, parsePage]);

  const reload = useCallback(() => {
    setLoaded(null);
    setReloadToken((current) => current + 1);
  }, []);

  return {
    items,
    isLoadingInitial,
    isLoadingMore,
    hasMore,
    hasError,
    loadMore,
    isSearchMode: debouncedQuery.length > 0,
    reload,
  };
}
